/**
 * 图改图(i2i)参考图上传 — 2026-05-25 自由创作改版 模式 B。
 *
 * 点击 / 拖拽上传一张图,作为 i2i base image(上传走 posterService.uploadRefImage,
 * 拿 objectKey 当 createTask.coverImageRef)。页面负责上传 mutation,这里只渲染交互 +
 * 预览 + 换图 / 清除。
 */
import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { PosterCard, PosterCardHeader, PosterSectionTitle } from "@/shared/ui/PosterCard";
import { IMAGE_ACCEPT } from "@/shared/lib/uploadImageFormats";

interface Props {
  previewUrl: string | null;
  uploading: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
  /**
   * 紧凑态:下面紧跟着 mask 画笔时用 —— 画笔本身就把原图放大显示了,这里再放一张
   * 大预览等于同一张图占两屏,把「涂哪改哪」挤到折叠线以下(正是用户提的"太靠下")。
   * 紧凑态只留一行:已挂图 + 换一张 / 移除。
   */
  compact?: boolean;
  /**
   * 2026-08-01 —— 底图**来自产品库**(商品主图那条路),不是用户传的。
   *
   * ★这条路没有上传口,却复用了 i2i 的机器 —— 机器是对的,身份表达不对:
   *   「上传一张图作为基底」「原图已挂上」都在说一件用户没做过的事。
   * ★同时**收掉清空按钮**:段控的「不用原图,重新画」已经是这件事的入口,
   *   两个控件干同一件事迟早漂(而且原来那个 ✕ 与「换一张」accessible name 撞车)。
   *   换自己的图这条逃生口留着,但要说清它是「改用我自己的图」。
   */
  productSourced?: boolean;
  /**
   * 2026-08-12 —— 同一个页面里挂**多个**上传口时的身份区分(换衣三步页:模特照 / 上装 / 下装)。
   *
   * ★三个槽共用一套 i18n 文案会变成三张一模一样的「参考图」卡:用户分不清哪个是哪个,
   *   测试也定位不到(三个同名 accessible name 直接撞 strict mode)。全部可选、
   *   缺省即原样 —— i2i 那条路一个字都不变。
   */
  title?: string;
  hint?: string;
  /** 空态按钮上那句话(缺省 = i18n 的「点击或拖拽上传图片」)。 */
  cta?: string;
  /** 隐藏 file input 的无障碍名。多槽时必给,否则读屏与测试都点不准哪个口。 */
  inputLabel?: string;
}

const ACCEPT = IMAGE_ACCEPT;

export function RefImageUpload({
  previewUrl,
  uploading,
  onPick,
  onClear,
  compact,
  productSourced,
  title,
  hint,
  cta,
  inputLabel,
}: Props) {
  const { t } = useTranslation("poster");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (file) onPick(file);
  };

  /** 换图按钮文案:产品路要说清「换成我自己的」,自由 i2i 就是普通的「换一张」。 */
  const replaceLabel = productSourced ? "改用我自己的图" : t("freeCreate.i2iReplace");
  /** 清空只在自由 i2i 出现 —— 产品路的清空归段控。 */
  const showClear = !productSourced;
  const cardTitle =
    title ?? (productSourced ? "底图 · 来自产品库" : t("freeCreate.i2iUploadTitle"));
  const cardHint =
    hint ??
    (productSourced
      ? "用这个商品的实拍图当底,下面写这张主图要什么样"
      : t("freeCreate.i2iUploadHint"));

  return (
    <PosterCard>
      <PosterCardHeader>
        <PosterSectionTitle title={cardTitle} hint={cardHint} />
      </PosterCardHeader>
      <div className="px-5 pb-5">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          {...(inputLabel ? { "aria-label": inputLabel } : {})}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {previewUrl && compact ? (
          <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-background/40 px-3 py-2">
            <img
              src={previewUrl}
              alt={cardTitle}
              className="h-9 w-9 rounded-md border border-border/60 object-cover"
            />
            <span className="text-[13px] text-muted-foreground">
              {productSourced
                ? "产品实拍图已挂上 · 在下面的画布上涂出要改的区域"
                : "原图已挂上 · 在下面的画布上涂出要改的区域"}
            </span>
            <div className="ml-auto flex gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-7 cursor-pointer px-2 text-[13px]"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
              >
                {replaceLabel}
              </Button>
              {showClear && (
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-7 w-7 cursor-pointer"
                  aria-label="移除这张底图"
                  disabled={uploading}
                  onClick={onClear}
                >
                  {uploading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
            </div>
          </div>
        ) : previewUrl ? (
          <div className="relative inline-block">
            <img
              src={previewUrl}
              alt={cardTitle}
              className="max-h-64 rounded-xl border border-border/60 object-contain"
            />
            <div className="absolute right-2 top-2 flex gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-7 px-2 text-[13px] cursor-pointer"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
              >
                {replaceLabel}
              </Button>
              {showClear && (
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-7 w-7 cursor-pointer"
                  aria-label="移除这张底图"
                  disabled={uploading}
                  onClick={onClear}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            {uploading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-background/60">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            className={cn(
              "flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-10 text-sm transition-colors cursor-pointer",
              dragOver
                ? "border-primary bg-primary/5 text-primary"
                : "border-border/60 bg-background/40 text-muted-foreground hover:border-primary/40 hover:text-foreground"
            )}
          >
            {uploading ? (
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
            ) : (
              <ImagePlus className="h-7 w-7" />
            )}
            <span>{cta ?? t("freeCreate.i2iUploadCta")}</span>
          </button>
        )}
      </div>
    </PosterCard>
  );
}
